import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, LMS_KATEGORI } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { toast } from "sonner";
import { Play, CheckCircle2, ArrowLeft, Download, Award, XCircle, PenLine } from "lucide-react";
import { YouTubeWatcher, LmsEmpty } from "./LmsShared";

/* ============ Daftar Pelatihan (Guru) ============ */
export function LmsDaftarGuru() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kat, setKat] = useState("");
  const [appliedKat, setAppliedKat] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/lms/pelatihan"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout nav={NAV_GURU} title="Pelatihan LMS">
      <PageTitle title="Pelatihan LMS" subtitle="Tonton video → kerjakan tes → dapatkan sertifikat" />
      <div className="pp-card p-4 mb-4 flex flex-wrap items-end gap-2">
        <select className="pp-input max-w-xs" value={kat} onChange={(e) => setKat(e.target.value)} data-testid="lms-g-filter-kat">
          <option value="">Semua Kategori</option>
          {LMS_KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <button onClick={() => setAppliedKat(kat)} className="pp-btn-primary" data-testid="lms-g-filter-apply">Terapkan</button>
        <button onClick={() => { setKat(""); setAppliedKat(""); }} className="pp-btn-secondary" data-testid="lms-g-filter-reset">Reset</button>
      </div>
      {loading ? (
        <div className="pp-card p-10 text-center text-slate-400">Memuat…</div>
      ) : items.length === 0 ? (
        <div className="pp-card p-10"><LmsEmpty msg="Belum ada pelatihan tersedia." /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.filter((it) => !appliedKat || it.kategori === appliedKat).map((it) => {
            const pr = it.progres || {};
            const status = pr.lulus ? "Lulus" : (pr.attempts ? "Belum Lulus" : (pr.watched ? "Sudah Nonton" : "Belum Ditonton"));
            const badge = pr.lulus ? "pp-badge-success" : (pr.attempts ? "pp-badge-danger" : (pr.watched ? "pp-badge-warning" : "pp-badge-neutral"));
            return (
              <button key={it.id} onClick={() => nav(`/guru/lms/${it.id}`)} className="pp-card p-5 text-left hover:shadow-lg transition"
                data-testid={`lms-g-card-${it.id}`}>
                <div className="aspect-video mb-3 rounded-lg overflow-hidden bg-slate-100 relative">
                  <img src={`https://img.youtube.com/vi/${it.youtube_id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center"><Play className="w-6 h-6 text-indigo-600" /></div>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 line-clamp-2">{it.judul}</h3>
                  <div className="flex gap-1 shrink-0">
                    <span className="pp-badge-neutral">{it.kategori || "Umum"}</span>
                    <span className={badge}>{status}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.deskripsi}</p>
                <div className="text-xs text-slate-500 mt-3 flex items-center gap-3">
                  <span>{it.soal_count} soal · min. {it.min_skor}</span>
                  {pr.skor != null && <span className="font-semibold">Skor: {pr.skor}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

/* ============ Tonton + Tes (satu halaman flow) ============ */
export function LmsTontonGuru() {
  const nav = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [watched, setWatched] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [phase, setPhase] = useState("watch"); // watch | test | result
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/lms/pelatihan/${id}`);
      setData(data);
      const pr = data.progres || {};
      setWatched(!!pr.watched);
      if (pr.lulus) setPhase("result");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleEnded = async () => {
    if (watched) return;
    try { await api.post(`/lms/pelatihan/${id}/watched`); setWatched(true); toast.success("Video selesai — tombol tes aktif"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!data) return;
    if (Object.keys(answers).length < data.soal.length) {
      if (!window.confirm("Ada soal yang belum dijawab. Kirim jawaban sekarang?")) return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/lms/pelatihan/${id}/submit`, { jawaban: answers });
      setResult(res); setPhase("result"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const dlCert = async () => {
    const kode = data?.progres?.kode_sertifikat || result?.kode_sertifikat;
    if (!kode) return;
    try {
      const r = await api.get(`/lms/sertifikat/${kode}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `sertifikat-${kode}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <Layout nav={NAV_GURU} title="Pelatihan"><div className="pp-card p-10 text-center text-slate-400">Memuat…</div></Layout>;

  const pr = data.progres || {};
  const canTest = watched;

  return (
    <Layout nav={NAV_GURU} title="Pelatihan">
      <PageTitle
        title={data.judul}
        subtitle={data.deskripsi}
        action={<button onClick={() => nav("/guru/lms")} className="pp-btn-secondary"><ArrowLeft className="w-4 h-4" /> Kembali</button>}
      />

      {phase === "watch" && (
        <div className="pp-card p-5 space-y-4" data-testid="lms-watch-panel">
          <YouTubeWatcher videoId={data.youtube_id} onEnded={handleEnded} onProgress={setProgressPct} />
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              {watched
                ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> <span className="text-emerald-700 font-semibold">Video selesai — Anda boleh mulai tes.</span></>
                : <><span className="text-slate-500">Menonton… {Math.round(progressPct*100)}%</span>
                    <span className="text-slate-400 text-xs">(Tombol tes aktif setelah video selesai atau ≥95%.)</span></>}
            </div>
            <button onClick={() => setPhase("test")} disabled={!canTest}
              className={canTest ? "pp-btn-primary" : "pp-btn-secondary opacity-60 cursor-not-allowed"}
              data-testid="lms-mulai-tes">
              <PenLine className="w-4 h-4" /> Mulai Tes ({data.soal.length} soal)
            </button>
          </div>
        </div>
      )}

      {phase === "test" && (
        <div className="pp-card p-6" data-testid="lms-test-panel">
          <h3 className="font-semibold mb-1">Tes — {data.judul}</h3>
          <p className="text-sm text-slate-500 mb-5">Pilih satu jawaban terbaik untuk tiap soal. Nilai minimal kelulusan: <b>{data.min_skor}</b>.</p>
          <div className="space-y-5">
            {data.soal.map((s, i) => (
              <div key={s.id} className="rounded-xl border border-slate-200 p-4" data-testid={`lms-q-${s.id}`}>
                <div className="font-medium text-slate-900 mb-3"><span className="text-indigo-600 mr-2">#{i+1}</span>{s.pertanyaan}</div>
                <div className="space-y-2">
                  {["a","b","c","d"].map((k) => (
                    <label key={k} className={`flex gap-3 p-3 rounded-lg cursor-pointer border ${answers[s.id]===k ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <input type="radio" name={s.id} checked={answers[s.id]===k}
                        onChange={() => setAnswers({ ...answers, [s.id]: k })} data-testid={`lms-opt-${s.id}-${k}`} />
                      <span className="font-semibold w-5">{k.toUpperCase()}.</span>
                      <span className="flex-1">{s.opsi[k]}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="pt-5 mt-5 border-t border-slate-200 flex justify-between">
            <button onClick={() => setPhase("watch")} className="pp-btn-secondary">Kembali menonton</button>
            <button onClick={submit} disabled={submitting} className="pp-btn-primary" data-testid="lms-submit-tes">
              {submitting ? "Mengirim…" : "Kirim Jawaban"}
            </button>
          </div>
        </div>
      )}

      {phase === "result" && (
        <div className="pp-card p-8 text-center" data-testid="lms-result-panel">
          {(result?.lulus ?? pr.lulus) ? (
            <>
              <Award className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-emerald-700">Selamat, Anda LULUS!</h3>
              <p className="text-slate-600 mt-1">Skor akhir Anda: <b>{result?.skor ?? pr.skor}</b> (minimal {data.min_skor})</p>
              <button onClick={dlCert} className="pp-btn-primary mt-6" data-testid="lms-dl-cert">
                <Download className="w-4 h-4" /> Unduh Sertifikat PDF
              </button>
            </>
          ) : (
            <>
              <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-rose-700">Belum Lulus</h3>
              <p className="text-slate-600 mt-1">Skor Anda: <b>{result?.skor ?? pr.skor}</b> — minimal {data.min_skor}. Silakan ulangi.</p>
              <div className="flex justify-center gap-2 mt-6">
                <button onClick={() => { setAnswers({}); setPhase("test"); }} className="pp-btn-primary" data-testid="lms-retry-tes">Ulangi Tes</button>
                <button onClick={() => setPhase("watch")} className="pp-btn-secondary">Tonton Ulang</button>
              </div>
            </>
          )}
        </div>
      )}
    </Layout>
  );
}
